import { useQuery } from "@tanstack/react-query";
import { fetchRecentCanvases, type RecentCanvasItem } from "../../lib/canvas-api";
import { generateCanvasId } from "../../lib/canvas-id";
import { navigate } from "../../router";
import { RecentCanvasCard } from "./recent-canvas-card";

// ダッシュボード（TOP）画面。
// - ようこそメッセージ
// - 新規キャンバスボタン（クリックで nanoid で URL 生成し /c/:canvasId へ遷移）
// - 最近使ったキャンバス一覧（プレビュー付き）
//
// 背景には MEIDEN CONNECT ログイン画面と同じ画像（bg-light.png）を使用し、
// ログイン直後の遷移で視覚的な連続性を保つ。本アプリは現状ライトモード固定のため
// ライト用画像のみを inline style で指定する。
export function DashboardView(): JSX.Element {
  const handleCreate = (): void => {
    const canvasId = generateCanvasId();
    navigate(`/c/${canvasId}`);
  };

  const recentCanvasesQuery = useQuery({
    queryKey: ["recent-canvases"],
    queryFn: fetchRecentCanvases,
    // 履歴の更新は他タブの操作でも起きるため、TOP に戻ったタイミングで再取得する。
    refetchOnMount: true,
  });
  const recentCanvases: readonly RecentCanvasItem[] = recentCanvasesQuery.data?.items ?? [];

  return (
    <div
      data-testid="dashboard-root"
      style={{
        position: "absolute",
        inset: 0,
        overflow: "auto",
        backgroundImage: "url(/bg-light.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <main
        style={{
          padding: "48px 64px",
          maxWidth: "1280px",
          margin: "0 auto",
          minHeight: "100%",
        }}
      >
        <header
          style={{
            marginBottom: "32px",
            background: "rgba(255, 255, 255, 0.78)",
            backdropFilter: "blur(8px)",
            borderRadius: "var(--radius-3)",
            padding: "24px 32px",
            boxShadow: "var(--shadow-1)",
          }}
        >
          <h1 style={{ fontSize: "32px", margin: 0 }}>リファインメントキャンバスへようこそ</h1>
          <p style={{ color: "var(--color-text-muted)", marginTop: "8px" }}>
            プロダクトバックログ・リファインメントを共同編集で進めるためのお絵かきツールです。
            新規キャンバスを作成するか、最近使ったキャンバスを開いてください。
          </p>
        </header>

        <button
          type="button"
          onClick={handleCreate}
          style={{
            padding: "16px 32px",
            borderRadius: "var(--radius-2)",
            border: "none",
            background: "var(--color-accent)",
            color: "var(--color-accent-contrast)",
            fontSize: "16px",
            fontWeight: 600,
            marginBottom: "32px",
            boxShadow: "var(--shadow-2)",
          }}
        >
          + 新規キャンバスを作成
        </button>

        <section
          style={{
            background: "rgba(255, 255, 255, 0.78)",
            backdropFilter: "blur(8px)",
            borderRadius: "var(--radius-3)",
            padding: "24px 32px",
            boxShadow: "var(--shadow-1)",
          }}
        >
          <h2 style={{ fontSize: "20px", marginBottom: "16px", marginTop: 0 }}>
            最近使ったキャンバス
          </h2>
          {recentCanvasesQuery.isLoading ? (
            <p style={{ color: "var(--color-text-muted)" }}>読み込み中…</p>
          ) : recentCanvasesQuery.isError ? (
            <p style={{ color: "var(--color-danger)" }}>
              履歴の取得に失敗しました。時間をおいて再読み込みしてください。
            </p>
          ) : recentCanvases.length === 0 ? (
            <p style={{ color: "var(--color-text-muted)" }}>
              まだ最近使ったキャンバスはありません。
            </p>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                gap: "16px",
              }}
            >
              {recentCanvases.map((c) => (
                <RecentCanvasCard
                  key={c.canvasId}
                  canvasId={c.canvasId}
                  title={c.title}
                  updatedAt={c.lastOpenedAt}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
